import { useRef } from "react";
import classnames from "classnames";

import { useWidth } from "../../../../hooks";
import { range } from "../../../../utils";
import { LocalEdit, LimitedInput } from "../../../../ui";
import { ImageContainer } from "../../../../media";
import layouts from "./layouts";
import style from "./style.module.scss";
import type { PageOptions, PhotoValue } from "../../../types";

interface LayoutProps {
  page?: PageOptions;
  onChange: (page: PageOptions) => void;
  productId?: string;
  userId?: string;
}

interface LayoutPreviewProps {
  page?: PageOptions;
}

export function Layout({ page = {}, onChange, productId, userId }: LayoutProps) {
  const ref = useRef<HTMLDivElement>(null);
  const height = useWidth(ref);
  const photos = page?.photos || {};
  const labels = page?.labels || {};
  const selectedLayout = layouts.find((layout) => layout.value === (page.layout || "margin"))!;
  const changePhoto = (index: number) => (url: PhotoValue) =>
    onChange({ ...page, photos: { ...photos, [index]: url } });
  const changeLabel = (index: number) => (value: string) =>
    onChange({
      ...page,
      labels: { ...labels, [index]: value },
    });
  return (
    <div
      ref={ref}
      style={{ height: height ?? undefined }}
      className={classnames(style.container, selectedLayout.style.container)}
    >
      {range(selectedLayout.countPhotos).map((index) => (
        <ImageContainer
          key={"photo-" + index}
          image={photos[index]}
          onChange={changePhoto(index)}
          productId={productId}
          userId={userId}
          className={selectedLayout.style["photo" + index]}
          imageClassName={selectedLayout.style["photoInner" + index]}
          aspectRatio={selectedLayout.photoAspectRatios?.[index] ?? 1}
          cropShape={selectedLayout.photoCropShapes?.[index] ?? "rect"}
        />
      ))}
      {range(selectedLayout.countLabels).map((index) => (
        <div
          key={"label-" + index}
          className={classnames(style.label, selectedLayout.style["label" + index])}
        >
          <LocalEdit
            value={labels[index] || ""}
            onChange={changeLabel(index)}
            render={({ value, onChange, onPersist }) => (
              <LimitedInput
                type="textarea"
                className={classnames(
                  style.label,
                  selectedLayout.style.label,
                  selectedLayout.style["label" + index],
                )}
                value={value}
                placeholder="Mon image..."
                maxLength={selectedLayout.labelMaxLength}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onPersist}
              />
            )}
          />
        </div>
      ))}
    </div>
  );
}

export const LayoutPreview = ({ page = {} }: LayoutPreviewProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const height = useWidth(ref);
  const photos = page?.photos || {};
  const labels = page?.labels || {};
  const selectedLayout = layouts.find((layout) => layout.value === (page.layout || "margin"))!;
  return (
    <div
      ref={ref}
      style={{ height: height ?? undefined }}
      className={classnames(style.container, selectedLayout.style.container)}
    >
      {range(selectedLayout.countPhotos).map((index) => (
        <ImageContainer
          key={"photo-" + index}
          image={photos[index]}
          className={selectedLayout.style["photo" + index]}
          imageClassName={selectedLayout.style["photoInner" + index]}
          isPreview
        />
      ))}
      {range(selectedLayout.countLabels).map((index) => (
        <div
          key={"label-" + index}
          className={classnames(style.label, selectedLayout.style["label" + index])}
        >
          {labels[index]}
        </div>
      ))}
    </div>
  );
};
